import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Plus, Trash2, Mic2, Wand2, Copy, Check, ArrowRight, Zap, AlertTriangle, GripVertical, Info } from "lucide-react";
import { toast } from "sonner";
import { Waveform } from "@/components/landing/Waveform";

const fmtChars = (n) => n >= 1000 ? `${(n/1000).toFixed(1).replace(/\.0$/,"")}k` : String(n);

const TONES = [
    { id: "news", label: "Standard news" },
    { id: "breaking", label: "Breaking news" },
    { id: "feature", label: "Feature story" },
    { id: "sports", label: "Sports" },
    { id: "calm", label: "Editorial / calm" },
    { id: "podcast", label: "Podcast" },
    { id: "dramatic", label: "Dramatic" },
    { id: "motivational", label: "Motivational" },
    { id: "friendly", label: "Friendly" },
    { id: "storytelling", label: "Storytelling" },
    { id: "formal", label: "Formal" },
];

const EMPTY_SEGMENT = () => ({ text: "", voice_id: "noor", tone: "news", id: Math.random().toString(36).slice(2) });

export default function ArticleMultiVoice() {
    const { user } = useAuth();
    const [title, setTitle] = useState("");
    const [language, setLanguage] = useState("Bengali");
    const LANGUAGES = ["Bengali", "English", "Arabic (Gulf)", "Spanish", "French", "Hindi"];
    const [segments, setSegments] = useState([EMPTY_SEGMENT()]);
    const [podcastMode, setPodcastMode] = useState(false);
    const [voices, setVoices] = useState([]);
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(false);
    const [genStage, setGenStage] = useState(0);
    const [articleData, setArticleData] = useState(null);
    const [copied, setCopied] = useState(false);
    const pollRef = useRef(null);
    const plan = user?.plan || "free";
    const isFreePlan = plan === "free";

    useEffect(() => {
        api.get("/voices").then(r => setVoices(r.data)).catch(() => {});
        api.get("/billing/credits").then(r => setCredits(r.data)).catch(() => {});
    }, []);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const rawChars = segments.reduce((s, seg) => s + seg.text.length, 0);
    const charCount = podcastMode ? Math.ceil(rawChars * 1.2) : rawChars;
    const charsAvailable = credits ? (credits.monthly_chars_remaining + credits.topup_chars_remaining) : Infinity;
    const insufficientCredits = credits && charCount > charsAvailable;
    const creditsLow = credits && charsAvailable > 0 && charsAvailable < 5000;

    const addSegment = () => {
        if (segments.length >= 20) { toast.error("Maximum 20 segments per article."); return; }
        setSegments(s => [...s, EMPTY_SEGMENT()]);
    };

    const removeSegment = (idx) => {
        if (segments.length <= 1) { toast.error("Need at least one segment."); return; }
        setSegments(s => s.filter((_, i) => i !== idx));
    };

    const updateSegment = (idx, key, val) => {
        setSegments(s => s.map((seg, i) => i === idx ? { ...seg, [key]: val } : seg));
    };

    const onCopyEmbed = async (aid) => {
        const code = `<script src="https://audo.dev/embed/v1.js" data-article="${aid}"></script>`;
        try { await navigator.clipboard.writeText(code); } catch {}
        setCopied(true);
        toast.success("Embed code copied!");
        setTimeout(() => setCopied(false), 2200);
    };

    const submit = async (e) => {
        e.preventDefault();
        const filled = segments.filter(s => s.text.trim());
        if (filled.length === 0) { toast.error("Add some text to at least one segment."); return; }
        if (insufficientCredits) { toast.error("Not enough credits."); return; }

        setLoading(true);
        setGenStage(1);
        try {
            const { data } = await api.post("/articles/multi-voice", {
                title: title.trim(),
                language: language,
                segments: segments.filter(s => s.text.trim()).map(s => ({
                    text: s.text.trim(),
                    voice_id: s.voice_id,
                    tone: isFreePlan ? "news" : s.tone,
                })),
                podcast_mode: podcastMode,
            });
            setArticleData(data);
            setGenStage(2);
            api.get("/billing/credits").then(r => setCredits(r.data)).catch(() => {});
            toast.success("Submitted — audio is generating in the background");
            pollRef.current = setInterval(async () => {
                try {
                    const { data: upd } = await api.get(`/articles/${data.id}`);
                    if (upd.status !== "processing") {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        setArticleData(upd);
                        setGenStage(3);
                        if (upd.status === "ready") toast.success("Multi-voice audio ready!");
                    }
                } catch {}
            }, 4000);
        } catch (err) {
            const msg = getErrorMessage(err);
            if (msg.includes("Insufficient") || err?.response?.status === 402) {
                toast.error("Not enough credits. Go to Billing.", { duration: 6000 });
            } else {
                toast.error(msg);
            }
            setGenStage(0);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="article-multiv-page">
            <PageHeader
                eyebrow="New article · Multiple voices"
                title="Multi-voice audio."
                subtitle="Each segment plays in the selected voice — merged into one seamless audio file."
            />

            {/* Credits bar */}
            {credits && (
                <div className={`mb-5 flex items-center gap-3 px-5 py-3 rounded-2xl text-[13px] ${
                    insufficientCredits ? "bg-red-50 border border-red-200 text-red-700" :
                    creditsLow ? "bg-amber-50 border border-amber-200 text-amber-800" :
                    "bg-mint/10 border border-mint/30 text-ink"
                }`}>
                    {insufficientCredits
                        ? <AlertTriangle size={14} className="flex-shrink-0" />
                        : creditsLow
                        ? <AlertTriangle size={14} className="flex-shrink-0 text-amber-600" />
                        : <Zap size={14} className="text-mint flex-shrink-0" />}
                    <span>
                        <strong>{fmtChars(credits.monthly_chars_remaining)} chars</strong> monthly remaining
                        {credits.topup_chars_remaining > 0 && <> · <strong className="text-mint">{fmtChars(credits.topup_chars_remaining)} top-up</strong></>}
                        {" · "}Total text <strong>{rawChars.toLocaleString()} chars</strong>
                        {podcastMode && <> → <strong className="text-coral">{charCount.toLocaleString()} chars billed</strong> (podcast +20%)</>}
                    </span>
                    {(insufficientCredits || creditsLow) && (
                        <Link to="/dashboard/billing" className="ml-auto text-[12px] font-semibold underline whitespace-nowrap">
                            Buy credits →
                        </Link>
                    )}
                </div>
            )}

            <form onSubmit={submit} className="space-y-5">
                {/* Title */}
                <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-2">Article title</label>
                    <input
                        required value={title} onChange={e => setTitle(e.target.value)}
                        placeholder="e.g. বাংলাদেশের অর্থনীতি — বিশেষ প্রতিবেদন"
                        className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                    />
                </div>

                {/* Language */}
                <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <div className="flex items-center gap-2 mb-4">
                        <svg className="w-[14px] h-[14px] text-coral" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Language</span>
                    </div>
                    <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                    >
                        {LANGUAGES.map(lang => (
                            <option key={lang} value={lang}>{lang}</option>
                        ))}
                    </select>
                </div>

                {/* Podcast mode toggle */}
                <div className="flex items-center justify-between bg-white rounded-3xl border border-cream-300 shadow-soft px-6 py-4">
                    <div className="flex items-start gap-3">
                        <Info size={14} className="text-coral mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-[14px] font-semibold text-ink">Podcast mode</p>
                            <p className="text-[12px] text-ink-muted">Adds richer intonation and natural pacing between speakers. Uses <strong className="text-coral">20% more characters</strong>.</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setPodcastMode(m => !m)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${podcastMode ? "bg-coral" : "bg-cream-300"}`}
                    >
                        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${podcastMode ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                </div>

                {/* Segments */}
                <div className="bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
                    <div className="px-6 py-4 border-b border-cream-300 flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Voice segments <span className="text-ink">{segments.length}/20</span></p>
                        <p className="text-[12px] text-ink-muted">Each row = one speaker block</p>
                    </div>

                    <div className="divide-y divide-cream-300">
                        {segments.map((seg, idx) => (
                            <div key={seg.id} className="p-5 flex gap-4 items-start group">
                                <div className="flex-shrink-0 mt-3 text-ink-muted opacity-40 group-hover:opacity-70 cursor-grab">
                                    <GripVertical size={16} />
                                </div>
                                <div className="w-6 h-6 rounded-full bg-coral/10 text-coral flex items-center justify-center flex-shrink-0 mt-3 text-[11px] font-bold">
                                    {idx + 1}
                                </div>
                                <div className="flex-1 space-y-3">
                                    <textarea
                                        required
                                        rows={3}
                                        value={seg.text}
                                        onChange={e => updateSegment(idx, "text", e.target.value)}
                                        placeholder={`Segment ${idx + 1} text (Bengali)…`}
                                        className="bn w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px] leading-[1.6] resize-none focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                                    />
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                                            <Mic2 size={12} className="text-coral flex-shrink-0" />
                                            <select
                                                value={seg.voice_id}
                                                onChange={e => updateSegment(idx, "voice_id", e.target.value)}
                                                className="flex-1 bg-cream-100 border border-cream-300 rounded-xl px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                                            >
                                                {voices.length === 0 && <option value="noor">Noor — F · Dhaka · Warm</option>}
                                                {voices.map(v => (
                                                    <option key={v.id} value={v.id}>
                                                        {v.name} — {v.gender === "female" ? "F" : "M"} · {v.accent} · {v.tone}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {!isFreePlan && (
                                            <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                                                <Wand2 size={12} className="text-coral flex-shrink-0" />
                                                <select
                                                    value={seg.tone}
                                                    onChange={e => updateSegment(idx, "tone", e.target.value)}
                                                    className="flex-1 bg-cream-100 border border-cream-300 rounded-xl px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                                                >
                                                    {TONES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                                </select>
                                            </div>
                                        )}
                                        <span className="text-[11px] text-ink-muted">{seg.text.length} chars</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeSegment(idx)}
                                    disabled={segments.length <= 1}
                                    className="mt-3 p-1.5 rounded-lg text-ink-muted hover:text-red-500 hover:bg-red-50 transition disabled:opacity-30"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="px-5 py-4 border-t border-cream-300">
                        <button
                            type="button"
                            onClick={addSegment}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-cream-300 hover:border-coral hover:text-coral text-ink-muted text-[13px] font-medium transition"
                        >
                            <Plus size={13} /> Add segment
                        </button>
                    </div>
                </div>

                {/* Summary + submit */}
                <div className="bg-white rounded-3xl border border-cream-300 shadow-soft p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <div className="flex-1 space-y-1.5">
                        <p className="text-[14px] font-semibold text-ink">{segments.length} segment{segments.length !== 1 ? "s" : ""} · {rawChars.toLocaleString()} raw chars</p>
                        <p className="text-[13px] text-ink-muted">
                            Billed: <strong>{charCount.toLocaleString()} chars</strong>
                            {podcastMode && <span className="text-coral"> (includes 20% podcast surcharge)</span>}
                            {credits && <> · {fmtChars(charsAvailable)} available</>}
                        </p>
                        {isFreePlan && (
                            <p className="text-[12px] text-amber-700">
                                Free plan: 2 voices + Standard news tone only. <Link to="/dashboard/billing" className="underline">Upgrade</Link> for more voices & tones.
                            </p>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={loading || genStage >= 2 || !title || rawChars === 0 || (credits && insufficientCredits)}
                        className="w-full sm:w-auto bg-ink hover:bg-coral text-cream-100 rounded-full px-8 py-3.5 text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition disabled:opacity-70 disabled:hover:bg-ink whitespace-nowrap"
                    >
                        {genStage === 1 ? <><Loader2 size={15} className="animate-spin" /> Submitting…</> : <><Wand2 size={14} /> Generate multi-voice audio</>}
                    </button>
                </div>
            </form>

            {/* Generation status popup */}
            {genStage >= 2 && articleData && (
                <div className="fixed bottom-6 right-6 bg-white rounded-3xl shadow-soft-lg border border-cream-300 p-5 w-[340px] z-50">
                    <div className="flex items-center gap-2 mb-3">
                        {genStage === 3
                            ? <span className="w-2 h-2 rounded-full bg-mint" />
                            : <span className="w-2 h-2 rounded-full bg-coral animate-pulse" />
                        }
                        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">
                            {genStage === 3 ? (articleData.status === "ready" ? "Audio ready!" : "No audio generated") : "Generating audio…"}
                        </span>
                    </div>
                    <div className="h-8 mb-3">
                        <Waveform bars={28} color={genStage === 3 ? "#2EE8B5" : "#FF5B3E"} height={28} playing={genStage === 2} />
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Embed code</p>
                    <div className="relative bg-cream-100 rounded-2xl p-3 pr-10 mb-3">
                        <code className="text-[10px] font-mono text-ink break-all leading-relaxed">
                            {`<script src="https://audo.dev/embed/v1.js" data-article="${articleData.id}"></script>`}
                        </code>
                        <button
                            onClick={() => onCopyEmbed(articleData.id)}
                            className={`absolute top-2 right-2 p-1.5 rounded-lg transition-colors ${copied ? "bg-mint text-ink" : "bg-white hover:bg-coral hover:text-white text-ink-muted"}`}
                        >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                    </div>
                    <p className="text-[11px] text-ink-muted mb-3">
                        {genStage === 3 ? "Paste the embed above on your website." : "You can leave this page. Audio generates in the background."}
                    </p>
                    <Link to="/dashboard/articles" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-coral hover:underline">
                        Go to My Articles <ArrowRight size={12} />
                    </Link>
                </div>
            )}
        </div>
    );
}
